import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Plus, FileText, Users, CheckCircle, Clock, AlertCircle, CreditCard, Banknote, Wallet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Insurance = () => {
  const token = localStorage.getItem('mojo_token');
  
  const [showNewPolicyDialog, setShowNewPolicyDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(true);
  const [calculatedPremium, setCalculatedPremium] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [walletBalance, setWalletBalance] = useState(0);
  
  const [formData, setFormData] = useState({
    // Policyholder
    policyholder_name: '',
    policyholder_surname: '',
    policyholder_id: '',
    policyholder_dob: '',
    cell_number: '',
    street: '',
    suburb: '',
    province: 'Gauteng',
    post_code: '',
    
    // Policy config
    plan_type: 'single',
    cover_option: 'worker',
    employment_status: 'employed',
    
    // Dependents
    include_spouse: false,
    spouse_name: '',
    spouse_id: '',
    spouse_dob: '',
    
    num_children: 0,
    children: [],
    
    // Payment
    payment_method: 'card'
  });
  
  useEffect(() => {
    fetchPolicies();
    fetchWalletBalance();
  }, []);
  
  const validateAndPopulateFromID = async (idNumber) => {
    if (!idNumber || idNumber.length !== 13) {
      return;
    }
    
    try {
      toast.loading('Validating ID number...');
      
      const response = await axios.post(
        `${API}/id/validate`,
        { id_number: idNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.dismiss();
      
      if (response.data.valid) {
        // Extract names from full_name if available, or use first/last name pattern
        const dob = response.data.date_of_birth;
        const gender = response.data.gender;
        const citizenship = response.data.citizenship;
        
        // Update form with validated data
        setFormData(prev => ({
          ...prev,
          policyholder_dob: dob,
          // Keep name fields as entered by user since CheckID doesn't return names
        }));
        
        toast.success(`ID verified! ${gender}, DOB: ${dob}, ${citizenship}`);
      } else {
        toast.error(response.data.error || 'Invalid ID number');
        setFormData(prev => ({
          ...prev,
          policyholder_dob: ''
        }));
      }
    } catch (error) {
      toast.dismiss();
      console.error('ID validation error:', error);
      toast.error('Failed to validate ID number');
    }
  };
  
  const fetchWalletBalance = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWalletBalance(response.data.balance || 0);
    } catch (error) {
      console.error('Failed to fetch wallet balance', error);
      setWalletBalance(0);
    }
  };
  
  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${API}/insurance/policies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicies(response.data.policies || []);
    } catch (error) {
      toast.error('Failed to fetch policies');
    } finally {
      setLoadingPolicies(false);
    }
  };
  
  const calculatePremium = async () => {
    if (!formData.policyholder_dob) {
      toast.error('Please enter policyholder date of birth');
      return;
    }
    
    setCalculating(true);
    try {
      const response = await axios.post(
        `${API}/insurance/calculate-premium`,
        null,  // No request body, using query params
        {
          params: {
            plan_type: formData.plan_type,
            cover_option: formData.cover_option,
            employment_status: formData.employment_status,
            policyholder_dob: formData.policyholder_dob,
            num_children: parseInt(formData.num_children) || 0,
            include_spouse: formData.include_spouse
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setCalculatedPremium(response.data);
      toast.success(`Premium calculated: R${response.data.premium_breakdown.total_premium}`);
    } catch (error) {
      // Handle different error formats from FastAPI
      let errorMessage = 'Failed to calculate premium';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setCalculating(false);
    }
  };
  
  const handleRequestPayment = () => {
    // Validation before showing payment dialog
    if (!formData.policyholder_name || !formData.policyholder_surname || !formData.policyholder_id || !formData.policyholder_dob) {
      toast.error('Please fill in all policyholder details');
      return;
    }
    
    if (!calculatedPremium) {
      toast.error('Please calculate premium first');
      return;
    }
    
    // Show payment selection dialog
    setShowPaymentDialog(true);
  };
  
  const handlePaymentMethodSelected = async () => {
    setPaymentProcessing(true);
    
    try {
      // Build dependents array
      const dependents = [];
      
      if (formData.include_spouse && formData.spouse_name) {
        dependents.push({
          name: formData.spouse_name,
          id_number: formData.spouse_id || null,
          date_of_birth: formData.spouse_dob || null,
          relationship: 'spouse'
        });
      }
      
      formData.children.forEach(child => {
        if (child.name) {
          dependents.push({
            name: child.name,
            id_number: child.id_number || null,
            date_of_birth: child.dob || null,
            relationship: 'child'
          });
        }
      });
      
      // Prepare policy data
      const policyData = {
        policyholder_name: formData.policyholder_name,
        policyholder_surname: formData.policyholder_surname,
        policyholder_id: formData.policyholder_id,
        policyholder_dob: formData.policyholder_dob,
        cell_number: formData.cell_number,
        address: {
          street: formData.street,
          suburb: formData.suburb,
          province: formData.province,
          post_code: formData.post_code
        },
        plan_type: formData.plan_type,
        cover_option: formData.cover_option,
        employment_status: formData.employment_status,
        dependents: dependents,
        first_premium_amount: calculatedPremium.premium_breakdown.total_premium,
        payment_method: selectedPaymentMethod,
        popi_approved: true
      };
      
      // Call new payment-first endpoint
      const response = await axios.post(
        `${API}/insurance/initiate-policy-with-payment`,
        policyData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Success!
      toast.success(`Policy created successfully! Policy Number: ${response.data.policy_number}`);
      setShowPaymentDialog(false);
      setShowNewPolicyDialog(false);
      
      // Reset form
      setFormData({
        policyholder_name: '',
        policyholder_surname: '',
        policyholder_id: '',
        policyholder_dob: '',
        cell_number: '',
        street: '',
        suburb: '',
        province: 'Gauteng',
        post_code: '',
        plan_type: 'single',
        cover_option: 'worker',
        employment_status: 'employed',
        include_spouse: false,
        spouse_name: '',
        spouse_id: '',
        spouse_dob: '',
        num_children: 0,
        children: [],
        payment_method: 'card'
      });
      setCalculatedPremium(null);
      setSelectedPaymentMethod('cash');
      
      // Refresh policies list
      fetchPolicies();
      
    } catch (error) {
      let errorMessage = 'Failed to create policy';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setPaymentProcessing(false);
    }
  };
  
  
  const resetForm = () => {
    setFormData({
      policyholder_name: '',
      policyholder_surname: '',
      policyholder_id: '',
      policyholder_dob: '',
      cell_number: '',
      street: '',
      suburb: '',
      province: 'Gauteng',
      post_code: '',
      plan_type: 'single',
      cover_option: 'worker',
      employment_status: 'employed',
      include_spouse: false,
      spouse_name: '',
      spouse_id: '',
      spouse_dob: '',
      num_children: 0,
      children: [],
      payment_method: 'card'
    });
    setCalculatedPremium(null);
    setPaymentConfirmed(false);
  };
  
  const addChild = () => {
    if (formData.children.length >= 5) {
      toast.error('Maximum 5 children allowed');
      return;
    }
    setFormData({
      ...formData,
      num_children: formData.children.length + 1,
      children: [...formData.children, { name: '', id_number: '', dob: '' }]
    });
  };
  
  const updateChild = (index, field, value) => {
    const updatedChildren = [...formData.children];
    updatedChildren[index] = { ...updatedChildren[index], [field]: value };
    setFormData({ ...formData, children: updatedChildren });
  };
  
  const removeChild = (index) => {
    const updatedChildren = formData.children.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      num_children: updatedChildren.length,
      children: updatedChildren
    });
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Funeral Insurance</h2>
          <p className="text-gray-600">Sell funeral policies and earn commissions</p>
        </div>
        <Button onClick={() => setShowNewPolicyDialog(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" /> New Policy
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Policies Sold</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{policies.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Active Policies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {policies.filter(p => p.status === 'active' || p.status === 'pending_activation').length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Commissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              R{policies.reduce((sum, p) => {
                const originalRate = p.employment_status === 'employed' ? 0.3 : 0.2;
                return sum + (p.premium_amount * originalRate * 0.5);
              }, 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Policies List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Policies</CardTitle>
          <CardDescription>Funeral policies you've sold</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingPolicies ? (
            <div className="text-center py-8 text-gray-500">Loading policies...</div>
          ) : policies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="mx-auto h-12 w-12 text-gray-400 mb-2" />
              <p>No policies sold yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {policies.map((policy) => (
                <div key={policy.policy_id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{policy.policyholder_name} {policy.policyholder_surname}</h3>
                        {policy.status === 'active' && <CheckCircle className="h-4 w-4 text-green-600" />}
                        {policy.status === 'pending_activation' && <Clock className="h-4 w-4 text-yellow-600" />}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1 mt-1">
                        <p>Policy ID: {policy.policy_id}</p>
                        <p>Plan: {policy.plan_type.charAt(0).toUpperCase() + policy.plan_type.slice(1)} - {policy.cover_option}</p>
                        <p>Dependents: {policy.dependents.length}</p>
                        <p>Created: {new Date(policy.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold">R{policy.premium_amount.toFixed(2)}</div>
                      <div className="text-sm text-gray-600">Premium</div>
                      <div className="text-sm text-green-600 font-medium mt-1">
                        +R{(policy.premium_amount * (policy.employment_status === 'employed' ? 0.3 : 0.2) * 0.5).toFixed(2)} commission
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* New Policy Dialog */}
      <Dialog open={showNewPolicyDialog} onOpenChange={setShowNewPolicyDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Funeral Insurance Policy</DialogTitle>
            <DialogDescription>Capture customer details and create a new funeral policy</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Plan Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Plan Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Plan Type</Label>
                    <Select value={formData.plan_type} onValueChange={(v) => setFormData({ ...formData, plan_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="family">Family</SelectItem>
                        <SelectItem value="single_parent">Single Parent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Cover Option</Label>
                    <Select value={formData.cover_option} onValueChange={(v) => setFormData({ ...formData, cover_option: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="worker">Worker</SelectItem>
                        <SelectItem value="social_grant">Social Grant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Employment Status</Label>
                    <Select value={formData.employment_status} onValueChange={(v) => setFormData({ ...formData, employment_status: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employed">Employed</SelectItem>
                        <SelectItem value="not_employed">Not Employed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Policyholder Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Policyholder Details</CardTitle>
                <CardDescription>Enter the ID number first to validate and auto-populate details from CheckID</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* ID Number FIRST - triggers validation */}
                <div>
                  <Label>ID Number * <span className="text-xs text-gray-500">(Enter this first)</span></Label>
                  <div className="flex gap-2">
                    <Input 
                      value={formData.policyholder_id}
                      onChange={(e) => {
                        const idNumber = e.target.value;
                        setFormData({ ...formData, policyholder_id: idNumber });
                        // Auto-validate when 13 digits entered
                        if (idNumber.length === 13) {
                          validateAndPopulateFromID(idNumber);
                        }
                      }}
                      placeholder="8505125678089"
                      maxLength={13}
                      className="flex-1"
                    />
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => validateAndPopulateFromID(formData.policyholder_id)}
                      disabled={formData.policyholder_id.length !== 13}
                    >
                      Verify ID
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Date of birth will be auto-filled after ID verification</p>
                </div>
                
                {/* Date of Birth - Auto-populated from ID */}
                <div>
                  <Label>Date of Birth * <span className="text-xs text-green-600">(Auto-filled from ID)</span></Label>
                  <Input 
                    type="date"
                    value={formData.policyholder_dob}
                    onChange={(e) => setFormData({ ...formData, policyholder_dob: e.target.value })}
                    className={formData.policyholder_dob ? 'bg-green-50 border-green-300' : ''}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <Input 
                      value={formData.policyholder_name}
                      onChange={(e) => setFormData({ ...formData, policyholder_name: e.target.value })}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label>Surname *</Label>
                    <Input 
                      value={formData.policyholder_surname}
                      onChange={(e) => setFormData({ ...formData, policyholder_surname: e.target.value })}
                      placeholder="Doe"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Cell Number *</Label>
                  <Input 
                    value={formData.cell_number}
                    onChange={(e) => setFormData({ ...formData, cell_number: e.target.value })}
                    placeholder="+27821234567"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Street Address</Label>
                    <Input 
                      value={formData.street}
                      onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div>
                    <Label>Suburb</Label>
                    <Input 
                      value={formData.suburb}
                      onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
                      placeholder="Sandton"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Province</Label>
                    <Select value={formData.province} onValueChange={(v) => setFormData({ ...formData, province: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Gauteng">Gauteng</SelectItem>
                        <SelectItem value="Western Cape">Western Cape</SelectItem>
                        <SelectItem value="KwaZulu-Natal">KwaZulu-Natal</SelectItem>
                        <SelectItem value="Eastern Cape">Eastern Cape</SelectItem>
                        <SelectItem value="Free State">Free State</SelectItem>
                        <SelectItem value="Limpopo">Limpopo</SelectItem>
                        <SelectItem value="Mpumalanga">Mpumalanga</SelectItem>
                        <SelectItem value="North West">North West</SelectItem>
                        <SelectItem value="Northern Cape">Northern Cape</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Post Code</Label>
                    <Input 
                      value={formData.post_code}
                      onChange={(e) => setFormData({ ...formData, post_code: e.target.value })}
                      placeholder="2196"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Dependents */}
            {(formData.plan_type === 'family' || formData.plan_type === 'single_parent') && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Dependents</CardTitle>
                  <CardDescription>Add spouse and children (ID/DOB can be provided later)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.plan_type === 'family' && (
                    <div>
                      <div className="flex items-center space-x-2 mb-4">
                        <input
                          type="checkbox"
                          checked={formData.include_spouse}
                          onChange={(e) => setFormData({ ...formData, include_spouse: e.target.checked })}
                          className="h-4 w-4"
                        />
                        <Label>Include Spouse</Label>
                      </div>
                      
                      {formData.include_spouse && (
                        <div className="space-y-3 border-l-2 border-blue-200 pl-4">
                          <Input 
                            placeholder="Spouse Name"
                            value={formData.spouse_name}
                            onChange={(e) => setFormData({ ...formData, spouse_name: e.target.value })}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <Input 
                              placeholder="ID Number (optional)"
                              value={formData.spouse_id}
                              onChange={(e) => setFormData({ ...formData, spouse_id: e.target.value })}
                            />
                            <Input 
                              type="date"
                              placeholder="Date of Birth (optional)"
                              value={formData.spouse_dob}
                              onChange={(e) => setFormData({ ...formData, spouse_dob: e.target.value })}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <Label>Children (Max 5)</Label>
                      <Button size="sm" onClick={addChild} variant="outline">
                        <Plus className="h-3 w-3 mr-1" /> Add Child
                      </Button>
                    </div>
                    
                    {formData.children.map((child, index) => (
                      <div key={index} className="space-y-2 p-3 border rounded mb-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Child {index + 1}</span>
                          <Button size="sm" variant="ghost" onClick={() => removeChild(index)}>Remove</Button>
                        </div>
                        <Input 
                          placeholder="Child Name"
                          value={child.name}
                          onChange={(e) => updateChild(index, 'name', e.target.value)}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input 
                            placeholder="ID Number (optional)"
                            value={child.id_number}
                            onChange={(e) => updateChild(index, 'id_number', e.target.value)}
                          />
                          <Input 
                            type="date"
                            placeholder="DOB (optional)"
                            value={child.dob}
                            onChange={(e) => updateChild(index, 'dob', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Premium Calculation */}
            <Card className={calculatedPremium ? 'border-green-500' : ''}>
              <CardHeader>
                <CardTitle className="text-lg">Premium Calculation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  onClick={calculatePremium} 
                  disabled={calculating || !formData.policyholder_dob}
                  className="w-full"
                  variant="outline"
                >
                  {calculating ? 'Calculating...' : 'Calculate Premium'}
                </Button>
                
                {calculatedPremium && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span>Base Premium:</span>
                      <span className="font-semibold">R{calculatedPremium.premium_breakdown.base_premium.toFixed(2)}</span>
                    </div>
                    {calculatedPremium.premium_breakdown.spouse_premium > 0 && (
                      <div className="flex justify-between">
                        <span>Spouse Premium:</span>
                        <span className="font-semibold">R{calculatedPremium.premium_breakdown.spouse_premium.toFixed(2)}</span>
                      </div>
                    )}
                    {calculatedPremium.premium_breakdown.children_premium > 0 && (
                      <div className="flex justify-between">
                        <span>Children Premium:</span>
                        <span className="font-semibold">R{calculatedPremium.premium_breakdown.children_premium.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between text-lg font-bold">
                      <span>Total Premium:</span>
                      <span className="text-blue-600">R{calculatedPremium.premium_breakdown.total_premium.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600 font-medium">
                      <span>Your Commission ({calculatedPremium.commission_split.merchant_rate}):</span>
                      <span>+R{calculatedPremium.commission_split.merchant_commission.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Submit */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowNewPolicyDialog(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRequestPayment}
                disabled={!calculatedPremium || submitting}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                Collect Premium & Create Policy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Payment Confirmation Dialog */}
      {/* Payment Method Selection Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Payment Method</DialogTitle>
            <DialogDescription>
              Choose how the customer will pay the premium
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Premium Amount Display */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">Premium Amount:</span>
                <span className="text-2xl font-bold text-blue-600">
                  R{calculatedPremium?.premium_breakdown.total_premium.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Your Wallet Balance:</span>
                <span className={`font-semibold ${
                  walletBalance < (calculatedPremium?.premium_breakdown.total_premium || 0)
                    ? 'text-red-600'
                    : 'text-green-600'
                }`}>
                  R{walletBalance.toFixed(2)}
                </span>
              </div>
            </div>
            
            {/* Insufficient Balance Warning for Cash */}
            {selectedPaymentMethod === 'cash' && walletBalance < (calculatedPremium?.premium_breakdown.total_premium || 0) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800 mb-1">Insufficient Wallet Balance</p>
                    <p className="text-sm text-red-700">
                      Cash collections require equivalent balance in your wallet. 
                      Required: R{calculatedPremium?.premium_breakdown.total_premium.toFixed(2)}, 
                      Available: R{walletBalance.toFixed(2)}
                    </p>
                    <p className="text-sm text-red-700 mt-2">
                      Please top up your wallet or choose a different payment method.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Payment Method Selection */}
            <div className="space-y-3">
              <Label>Payment Method</Label>
              
              {/* Cash Option */}
              <div 
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedPaymentMethod === 'cash' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                } ${
                  walletBalance < (calculatedPremium?.premium_breakdown.total_premium || 0)
                    ? 'opacity-50'
                    : ''
                }`}
                onClick={() => setSelectedPaymentMethod('cash')}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPaymentMethod === 'cash' ? 'border-blue-500' : 'border-gray-300'
                  }`}>
                    {selectedPaymentMethod === 'cash' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">Cash</div>
                    <div className="text-sm text-gray-600">Collect physical cash from customer</div>
                    {walletBalance < (calculatedPremium?.premium_breakdown.total_premium || 0) && (
                      <div className="text-xs text-red-600 mt-1">⚠️ Insufficient wallet balance</div>
                    )}
                  </div>
                  <Banknote className="w-6 h-6 text-green-600" />
                </div>
              </div>
              
              {/* Card Option */}
              <div 
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedPaymentMethod === 'card' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPaymentMethod('card')}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPaymentMethod === 'card' ? 'border-blue-500' : 'border-gray-300'
                  }`}>
                    {selectedPaymentMethod === 'card' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">Card</div>
                    <div className="text-sm text-gray-600">Process card payment via Traderoute</div>
                  </div>
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              
              {/* Wallet Option */}
              <div 
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedPaymentMethod === 'wallet' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPaymentMethod('wallet')}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPaymentMethod === 'wallet' ? 'border-blue-500' : 'border-gray-300'
                  }`}>
                    {selectedPaymentMethod === 'wallet' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">Wallet</div>
                    <div className="text-sm text-gray-600">Pay from Mojo Money wallet</div>
                  </div>
                  <Wallet className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
            
            {/* Warning Message */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Policy number will be generated only after payment is confirmed.
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setShowPaymentDialog(false)}
              disabled={paymentProcessing}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePaymentMethodSelected}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={
                paymentProcessing || 
                (selectedPaymentMethod === 'wallet' && walletBalance < (calculatedPremium?.premium_breakdown.total_premium || 0))
              }
            >
              {paymentProcessing ? (
                <>Processing...</>
              ) : (
                selectedPaymentMethod === 'wallet' && walletBalance < (calculatedPremium?.premium_breakdown.total_premium || 0) ? (
                  <>Insufficient Wallet Balance</>
                ) : (
                  <>✓ Collect Payment & Create Policy</>
                )
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Insurance;
